import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, CircularProgress, FormControl, Grid, InputLabel,
  MenuItem, Paper, Select, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SendIcon from "@mui/icons-material/Send";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { Link as RouterLink } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import api from "../../api";
import { toast } from "react-toastify";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import AnalyticsFiltersBar from "./AnalyticsFiltersBar";
import useAnalyticsFilters from "./useAnalyticsFilters";
import AnalyticsDataTable from "./AnalyticsDataTable";
import AppUsersEngagement from "./AppUsersEngagement";

const TILE_CONFIGS = [
  { key: "total_student_users", label: "Student App Users",  Icon: PeopleIcon,      iconBg: "#ede9fe", iconColor: "#7c3aed" },
  { key: "active_users",        label: "Active This Week",   Icon: TrendingUpIcon,  iconBg: "#dcfce7", iconColor: "#16a34a" },
  { key: "weak_users",          label: "Weak App Users",     Icon: WarningAmberIcon, iconBg: "#fff7ed", iconColor: "#ea580c" },
];

function KpiTile({ label, value, Icon, iconBg, iconColor }) {
  return (
    <Paper sx={{ ...CARD_SX, display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ color: iconColor, fontSize: 26 }} />
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{(value ?? 0).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
      </Box>
    </Paper>
  );
}

function relativeTime(isoString) {
  if (!isoString) return "Never";
  try {
    return formatDistanceToNow(new Date(isoString), { addSuffix: true });
  } catch {
    return isoString;
  }
}

const PAGE_TITLE = "App Engagement & Email Marketing";
const PAGE_SUBTITLE = "Monitor app engagement, identify weak app users and run email marketing campaigns to re-engage students and parents.";

/** Group students (course students) or App users (trial and plan holders): two different groups of people. */
function ViewSwitch({ view, onChange }) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={view}
      onChange={(_, next) => next && onChange(next)}
      aria-label="Who to show"
      sx={{ mb: 2, "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 700, px: 2 } }}
    >
      <ToggleButton value="students">Group students</ToggleButton>
      <ToggleButton value="app">App users (trial &amp; plans)</ToggleButton>
    </ToggleButtonGroup>
  );
}

export default function AppUsageAnalytics() {
  const [view, setView] = useState("students");
  const switcher = <ViewSwitch view={view} onChange={setView} />;

  if (view === "app") {
    return (
      <AnalyticsPageShell title={PAGE_TITLE} subtitle="Who keeps using the app after signing up, and who has stopped.">
        {switcher}
        <AppUsersEngagement />
      </AnalyticsPageShell>
    );
  }

  return <GroupStudentsEngagement switcher={switcher} />;
}

function GroupStudentsEngagement({ switcher }) {
  const [courseId, setCourseId] = useState("");
  const [academicCourseId, setAcademicCourseId] = useState("");
  const [planId, setPlanId] = useState("");
  const [engagementLevel, setEngagementLevel] = useState("");
  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [audience, setAudience] = useState("weak_users");
  const [campaignType, setCampaignType] = useState("re_engagement");
  const [subject, setSubject] = useState("Stay on track with 11+ Magic");
  const [bodyHtml, setBodyHtml] = useState(
    "Hi [parent_name],\nWe noticed your child hasn't been active on the app recently. Encourage them to log in and continue their learning. Let's keep the momentum going!"
  );
  const [sending, setSending] = useState(false);

  const { courses, academicCourses, plans } = useAnalyticsFilters({ courseId, academicCourseId });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (courseId) params.course_id = courseId;
      if (academicCourseId) params.academic_course_id = academicCourseId;
      if (planId) params.course_plan_id = planId;
      if (engagementLevel) params.engagement_level = engagementLevel;
      const res = await api.get("admin/analytics/app-usage", { params });
      const d = res.data?.data || {};
      setSummary(d.summary || {});
      setRows(d.users || d.weak_users || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load app usage");
      setSummary({});
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [courseId, academicCourseId, planId, engagementLevel]);

  useEffect(() => { load(); }, [load]);

  const sendCampaign = async () => {
    try {
      setSending(true);
      await api.post("admin/analytics/app-usage/campaign", {
        subject,
        body_html: bodyHtml.replace(/\n/g, "<br>"),
        target_weak_only: audience === "weak_users",
      });
      toast.success("Campaign queued");
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to queue campaign");
    } finally {
      setSending(false);
    }
  };

  const columns = [
    { key: "student_name", label: "Student" },
    { key: "batch_name", label: "Group" },
    { key: "last_activity_at", label: "Last Active", render: (r) => relativeTime(r.last_activity_at) },
    { key: "weekly_sessions", label: "Weekly Sessions", render: (r) => r.weekly_sessions ?? 0 },
    { key: "parent_email", label: "Parent Email" },
  ];

  return (
    <AnalyticsPageShell
      title={PAGE_TITLE}
      subtitle={PAGE_SUBTITLE}
      filters={
        <AnalyticsFiltersBar
          courses={courses}
          academicCourses={academicCourses}
          plans={plans}
          courseId={courseId}
          setCourseId={setCourseId}
          showAcademicCourse={false}
          academicCourseId={academicCourseId}
          setAcademicCourseId={setAcademicCourseId}
          planId={planId}
          setPlanId={setPlanId}
          showEngagementLevel
          engagementLevel={engagementLevel}
          setEngagementLevel={setEngagementLevel}
        />
      }
    >
      {switcher}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {TILE_CONFIGS.map(({ key, label, Icon, iconBg, iconColor }) => (
              <Grid item xs={12} sm={4} key={key}>
                <KpiTile label={label} value={summary[key]} Icon={Icon} iconBg={iconBg} iconColor={iconColor} />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {engagementLevel === "active" ? "Active App Users" : "Weak App Users"}
              </Typography>
              <AnalyticsDataTable
                columns={columns}
                rows={rows}
                loading={false}
                renderActions={(row) => (
                  <Button
                    size="small"
                    variant="outlined"
                    component={RouterLink}
                    to={`/admin/student/${row.student_id}`}
                    sx={{ textTransform: "none" }}
                  >
                    View
                  </Button>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ ...CARD_SX, position: "sticky", top: 16 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Email Campaign</Typography>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Audience</InputLabel>
                  <Select label="Audience" value={audience} onChange={(e) => setAudience(e.target.value)}>
                    <MenuItem value="weak_users">Weak App Users</MenuItem>
                    <MenuItem value="all_students">All Students</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Campaign Type</InputLabel>
                  <Select label="Campaign Type" value={campaignType} onChange={(e) => setCampaignType(e.target.value)}>
                    <MenuItem value="re_engagement">Re-engagement</MenuItem>
                    <MenuItem value="reminder">Reminder</MenuItem>
                    <MenuItem value="announcement">Announcement</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  label="Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>Message Preview</Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  size="small"
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    disabled={sending || !rows.length}
                    onClick={sendCampaign}
                    sx={{ flex: 1, textTransform: "none" }}
                  >
                    Send Campaign
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ScheduleIcon />}
                    sx={{ flex: 1, textTransform: "none" }}
                    disabled
                  >
                    Schedule
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </AnalyticsPageShell>
  );
}
